import React, { useState } from 'react';
import { validateAndConsumeExamPin } from '../config/firebase';

export default function StudentExamAccessModal({ currentUser, isOpen, onClose, onStartExam }) {
  const [pinInput, setPinInput] = useState('');
  const [gradeInput, setGradeInput] = useState(currentUser?.grade || '6');
  const [unitInput, setUnitInput] = useState(1);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmitAccess = async (e) => {
    e.preventDefault();
    const cleanPin = pinInput.trim();
    if (!cleanPin || cleanPin.length < 6) {
      setErrorMessage("Por favor ingresa el PIN completo de 6 dígitos proyectado por el profesor.");
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const studentName = currentUser ? currentUser.username : 'Estudiante EOH';
    const studentCode = currentUser ? currentUser.code : '';

    try {
      const result = await validateAndConsumeExamPin({
        pinInput: cleanPin,
        gradeInput: gradeInput,
        unitInput: Number(unitInput),
        studentName: studentName,
        studentCode: studentCode
      });

      setLoading(false);

      if (result.success) {
        // Iniciar el examen con el Grado y Unidad configurados por el docente en el PIN
        onStartExam({
          grade: String(result.pinData?.grade || gradeInput),
          unit: Number(result.pinData?.unit || unitInput),
          pinData: result.pinData
        });
        onClose();
      } else {
        setErrorMessage(result.reason || "Error al validar el PIN. Inténtalo de nuevo.");
      }
    } catch (err) {
      setLoading(false);
      setErrorMessage("Error de conexión al verificar el PIN en la nube. Verifica tu conexión a internet.");
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-slate-900 border border-purple-500/50 rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl space-y-6 text-left">
        
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white font-bold text-lg bg-slate-800 w-8 h-8 rounded-full flex items-center justify-center transition cursor-pointer"
        >
          ✕
        </button>

        <div className="space-y-1">
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-purple-400 bg-purple-950 px-2.5 py-0.5 rounded-full border border-purple-500/30">
            Prueba Automatizada ICFES / Saber
          </span>
          <h3 className="text-xl font-black text-white mt-1">
            📝 Acceso a Evaluación Sincronizada
          </h3>
          <p className="text-xs text-slate-300">
            Ingresa el PIN de 6 dígitos que el profesor tiene proyectado en el salón para desbloquear tu examen.
          </p>
          <div className="bg-amber-950/60 border border-amber-500/40 p-2.5 rounded-xl text-[11px] text-amber-200 flex items-center gap-2 mt-2">
            <span>🛡️</span>
            <span><strong>Control de Enfoque Activo:</strong> La prueba detecta salidas de la app (máximo 2 advertencias).</span>
          </div>
        </div>

        <form onSubmit={handleSubmitAccess} className="space-y-4">
          
          {/* CAMPO: PIN DE ACCESO ACTIVO */}
          <div>
            <label className="text-[10px] font-bold text-slate-300 uppercase tracking-wider block mb-1">
              PIN de Acceso Activo (6 dígitos):
            </label>
            <input 
              type="text"
              maxLength={6}
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
              placeholder="Ej: 849201"
              autoFocus
              className="w-full bg-slate-950 border border-purple-500/60 rounded-xl px-4 py-3 text-2xl font-mono font-black text-center text-purple-300 tracking-widest outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30 shadow-inner"
              required
            />
            <span className="text-[10px] text-slate-400 block mt-1 text-center">
              Código temporal proyectado por el docente en pantalla.
            </span>
          </div>

          {/* SELECTORES OPCIONALES DE RESPALDO */}
          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-800">
            <div>
              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                Grado:
              </label>
              <select 
                value={gradeInput}
                onChange={(e) => setGradeInput(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-2 text-xs font-bold text-slate-300 outline-none focus:border-purple-500 cursor-pointer"
              >
                <option value="6">Grado 6°</option>
                <option value="7">Grado 7°</option>
                <option value="9">Grado 9°</option>
              </select>
            </div>

            <div>
              <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                Unidad:
              </label>
              <select 
                value={unitInput}
                onChange={(e) => setUnitInput(Number(e.target.value))}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-2 text-xs font-bold text-slate-300 outline-none focus:border-purple-500 cursor-pointer"
              >
                <option value={1}>Unidad 1</option>
                <option value={2}>Unidad 2</option>
                <option value={3}>Unidad 3</option>
                <option value={4}>Unidad 4</option>
                <option value={5}>Unidad 5</option>
              </select>
            </div>
          </div>

          {/* MENSAJE DE ERROR */}
          {errorMessage && (
            <div className="bg-red-950/90 border border-red-500/60 p-3 rounded-xl text-xs text-red-200 font-bold leading-relaxed animate-fadeIn">
              ⚠️ {errorMessage}
            </div>
          )}

          {/* BOTÓN SUBMIT */}
          <button
            type="submit"
            disabled={loading || pinInput.length < 6}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:opacity-40 text-white font-extrabold py-3.5 rounded-xl transition shadow-xl text-xs flex items-center justify-center gap-2 cursor-pointer mt-2"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Verificando PIN en la nube...
              </span>
            ) : (
              'Validar PIN e Iniciar Evaluación 🚀'
            )}
          </button>
        </form>

      </div>
    </div>
  );
}
